import { useState, useEffect } from 'react';
import { Package, AlertTriangle, Search, TrendingDown, ShoppingCart, DollarSign, Archive, BarChart3 } from 'lucide-react';
import useProductStore from '../../stores/useProductStore';

/**
 * Modern Inventory Page - مخزون السيارة
 * Beautiful, mobile-optimized inventory management
 *
 * Features:
 * - Colorful statistics cards with gradient backgrounds
 * - Smart alerts with color coding (red, yellow, green)
 * - Product cards with status indicators
 * - Search & Filter capabilities
 * - Smooth animations and transitions
 */

const Inventory = () => {
  const {
    vehicleInventory,
    vehicleStatistics,
    loading,
    error,
    fetchMyVehicleInventory,
  } = useProductStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statistics, setStatistics] = useState({
    totalItems: 0,
    totalQuantity: 0,
    totalValue: 0,
    outOfStockCount: 0,
    lowStockCount: 0,
    availableItems: 0
  });

  // Load vehicle inventory on mount
  useEffect(() => {
    loadVehicleInventoryData();
  }, []);

  const loadVehicleInventoryData = async () => {
    try {
      const result = await fetchMyVehicleInventory();
      if (result && result.statistics) {
        setStatistics({
          totalItems: result.statistics.total_items || 0,
          totalQuantity: result.statistics.total_quantity || 0,
          totalValue: result.statistics.total_value || 0,
          outOfStockCount: result.statistics.out_of_stock_items || 0,
          lowStockCount: result.statistics.low_stock_items || 0,
          availableItems: result.statistics.available_items || 0
        });
      }
    } catch (err) {
      console.error('Failed to load vehicle inventory:', err);
    }
  };

  // Get stock status with colors
  const getStockStatus = (product) => {
    const qty = product.quantity ?? 0;
    const minQty = product.min_stock_level ?? 10;
    const status = product.stock_status || (
      qty === 0 ? 'out' :
      qty <= minQty ? 'low' :
      'normal'
    );

    const statusConfig = {
      normal: {
        label: 'متوفر',
        color: 'text-emerald-700 dark:text-emerald-400',
        bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/20',
        border: 'border-emerald-200 dark:border-emerald-700',
        badge: 'bg-emerald-500',
        icon: Package,
        iconColor: 'text-emerald-600 dark:text-emerald-400'
      },
      low: {
        label: 'قريب من النفاد',
        color: 'text-amber-700 dark:text-amber-400',
        bg: 'bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/20',
        border: 'border-amber-200 dark:border-amber-700',
        badge: 'bg-amber-500',
        icon: AlertTriangle,
        iconColor: 'text-amber-600 dark:text-amber-400'
      },
      out: {
        label: 'نفد المخزون',
        color: 'text-red-700 dark:text-red-400',
        bg: 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20',
        border: 'border-red-200 dark:border-red-700',
        badge: 'bg-red-500',
        icon: Archive,
        iconColor: 'text-red-600 dark:text-red-400'
      }
    };

    return { status, ...statusConfig[status] };
  };

  // Filter products
  const getFilteredProducts = () => {
    let filtered = vehicleInventory;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(product => {
        const { status } = getStockStatus(product);
        return status === statusFilter;
      });
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        (product.product_name || '').toLowerCase().includes(query) ||
        (product.product_code || '').toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 pb-24">

      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-800 dark:to-purple-800 px-4 py-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <Package className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">مخزون السيارة</h1>
            <p className="text-blue-100 text-sm">إدارة المنتجات المتاحة</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="ابحث عن منتج..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pr-12 pl-4 py-3 rounded-xl bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-0 shadow-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
          />
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">

        {/* Statistics Cards Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Total Items */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 rounded-2xl p-4 shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <ShoppingCart className="w-8 h-8 text-white/90" />
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {statistics.totalItems}
              </p>
              <p className="text-blue-100 text-sm font-medium">إجمالي الأصناف</p>
            </div>
          </div>

          {/* Total Quantity */}
          <div className="relative overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700 rounded-2xl p-4 shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-8 h-8 text-white/90" />
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {statistics.totalQuantity}
              </p>
              <p className="text-purple-100 text-sm font-medium">إجمالي الكمية</p>
            </div>
          </div>

          {/* Total Value */}
          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700 rounded-2xl p-4 shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-white/90" />
              </div>
              <p className="text-2xl font-bold text-white mb-1">
                {statistics.totalValue.toLocaleString('ar-KW', { maximumFractionDigits: 0 })}
              </p>
              <p className="text-emerald-100 text-sm font-medium">د.ك. قيمة المخزون</p>
            </div>
          </div>

          {/* Available Items */}
          <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700 rounded-2xl p-4 shadow-lg transform hover:scale-105 transition-transform duration-200">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-8 h-8 text-white/90" />
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {statistics.availableItems}
              </p>
              <p className="text-teal-100 text-sm font-medium">أصناف متوفرة</p>
            </div>
          </div>
        </div>

        {/* Alert Badges */}
        <div className="flex gap-2 flex-wrap">
          {statistics.outOfStockCount > 0 && (
            <div className="flex-1 min-w-[140px] bg-gradient-to-r from-red-500 to-red-600 dark:from-red-600 dark:to-red-700 rounded-xl px-4 py-3 shadow-lg">
              <div className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-white" />
                <div>
                  <p className="text-2xl font-bold text-white">{statistics.outOfStockCount}</p>
                  <p className="text-red-100 text-xs font-medium">نفد من المخزون</p>
                </div>
              </div>
            </div>
          )}

          {statistics.lowStockCount > 0 && (
            <div className="flex-1 min-w-[140px] bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 rounded-xl px-4 py-3 shadow-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-white" />
                <div>
                  <p className="text-2xl font-bold text-white">{statistics.lowStockCount}</p>
                  <p className="text-amber-100 text-xs font-medium">قريب من النفاد</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-200 ${
              statusFilter === 'all'
                ? 'bg-blue-600 text-white shadow-lg scale-105'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow'
            }`}
          >
            الكل ({vehicleInventory.length})
          </button>
          <button
            onClick={() => setStatusFilter('normal')}
            className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-200 ${
              statusFilter === 'normal'
                ? 'bg-emerald-600 text-white shadow-lg scale-105'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow'
            }`}
          >
            متوفر ({statistics.availableItems})
          </button>
          <button
            onClick={() => setStatusFilter('low')}
            className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-200 ${
              statusFilter === 'low'
                ? 'bg-amber-600 text-white shadow-lg scale-105'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow'
            }`}
          >
            قريب نفاد ({statistics.lowStockCount})
          </button>
          <button
            onClick={() => setStatusFilter('out')}
            className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all duration-200 ${
              statusFilter === 'out'
                ? 'bg-red-600 text-white shadow-lg scale-105'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow'
            }`}
          >
            نفد ({statistics.outOfStockCount})
          </button>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin"></div>
              <Package className="w-6 h-6 text-blue-600 dark:text-blue-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-3" />
            <p className="text-red-800 dark:text-red-300 font-medium">{error}</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 text-center shadow-lg">
            <Package className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">لا توجد منتجات</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProducts.map((product) => {
              const stockInfo = getStockStatus(product);
              const StatusIcon = stockInfo.icon;
              const qty = product.quantity || 0;
              const unitPrice = product.unit_price || 0;
              const totalValue = qty * unitPrice;

              return (
                <div
                  key={product.id}
                  className={`${stockInfo.bg} ${stockInfo.border} border-2 rounded-2xl p-4 shadow-md hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-12 h-12 ${stockInfo.badge} rounded-xl flex items-center justify-center shadow-lg`}>
                        <StatusIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg ${stockInfo.color} mb-1`}>
                          {product.product_name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {product.product_code || product.category || 'غير مصنف'}
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${stockInfo.badge} text-white shadow-md`}>
                      {stockInfo.label}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {/* Quantity */}
                    <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 backdrop-blur-sm">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">الكمية</p>
                      <p className={`text-2xl font-bold ${stockInfo.color}`}>
                        {qty}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        {product.unit || 'وحدة'}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 backdrop-blur-sm">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">السعر</p>
                      <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {unitPrice.toLocaleString('ar-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">د.ك.</p>
                    </div>

                    {/* Total Value */}
                    <div className="bg-white/60 dark:bg-gray-800/60 rounded-xl p-3 backdrop-blur-sm">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">الإجمالي</p>
                      <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                        {totalValue.toLocaleString('ar-KW', { maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">د.ك.</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative">
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${stockInfo.badge} transition-all duration-500 rounded-full`}
                        style={{
                          width: `${Math.min(100, (qty / (product.min_stock_level || 10)) * 100)}%`
                        }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                      الحد الأدنى: {product.min_stock_level || 10} {product.unit || 'وحدة'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Results Count */}
        {!loading && !error && filteredProducts.length > 0 && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              عرض <span className="font-bold text-blue-600 dark:text-blue-400">{filteredProducts.length}</span> من {vehicleInventory.length} صنف
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;
